/**
 * React dependencies
 */
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';

/**
 * External dependencies
 */
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { styled, alpha } from '@mui/material/styles';

/**
 * Local dependencies
 */
import { COLOR_BLACK, COLOR_GOLD_DARK, COLOR_RED, COLOR_WHITE } from 'constants/colors';
import { FIELD_BORDER_WIDTH, FIELD_SPACING } from 'constants/forms';

const CHEVRON_BOX_WIDTH = '24px';
const CHEVRON_BOX_HEIGHT = '23px';
const CHEVRON_BOX_INSET = '10px';

/**
 * Uses MUI's TextField in `select` mode rather than a bare Select so that the
 * label, outline notch and helper text behave identically to the form's text
 * fields. A standalone Select renders no label and ignores helperText.
 */
const CssSelectField = styled(TextField)({
  marginBottom: FIELD_SPACING,
  color: COLOR_BLACK,
  fontWeight: 800,
  '& .MuiInputLabel-root': {
    color: COLOR_BLACK,

    fontFamily: "'Montserrat', sans-serif",
    '&.Mui-error': {
      color: COLOR_BLACK,
    }
  },
  '& .MuiInputLabel-outlined': {
    transform: 'translate(1rem, 1rem) scale(1)',
    '&.MuiFormLabel-filled, &.Mui-focused': {
      transform: 'translate(14px, -6px) scale(0.75)',
      color: COLOR_BLACK,
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '0.9rem 1rem',
    color: COLOR_BLACK
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '0',
    fontFamily: "'Montserrat', sans-serif",
    backgroundColor: 'rgba(255,255,255,0)',
    textAlign: 'left',
    '& .MuiOutlinedInput-notchedOutline': {
      transition: 'background-color 0.2s ease',
    },
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        backgroundColor: 'rgba(255,255,255,0.1)'
      }
    },
    // MUI sets its own border width per state, so each is pinned to keep the
    // outline from thickening on hover or focus.
    '& fieldset': {
      borderColor: COLOR_BLACK,
      borderWidth: FIELD_BORDER_WIDTH,
    },
    '&:hover fieldset': {
      borderWidth: FIELD_BORDER_WIDTH,
    },
    '&.Mui-focused fieldset': {
      borderColor: COLOR_BLACK,
      borderWidth: FIELD_BORDER_WIDTH,
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
    '&.Mui-error': {
      color: COLOR_RED,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: COLOR_RED,
        borderWidth: FIELD_BORDER_WIDTH,
      },
    },
  },
  '& .MuiSelect-icon': {
    color: COLOR_BLACK,
    backgroundColor: COLOR_GOLD_DARK,
    width: CHEVRON_BOX_WIDTH,
    height: CHEVRON_BOX_HEIGHT,
    right: CHEVRON_BOX_INSET,
    top: `calc(50% - (${CHEVRON_BOX_HEIGHT} / 2))`,
    boxSizing: 'border-box',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '& svg': {
      marginTop: '2px',
      display: 'block',
      transition: 'transform 0.2s ease',
    },
  },
  '& .MuiSelect-iconOpen': {
    transform: 'none',

    '& svg': {
      transform: 'rotate(180deg)',
    },
  },
  '& .Mui-error': {
    color: COLOR_RED,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: COLOR_RED,
      borderWidth: FIELD_BORDER_WIDTH,
    },
  },
  '& .MuiFormHelperText-root.Mui-error': {
    margin: '0 0 0.5rem',
    backgroundColor: COLOR_RED,
    color: COLOR_WHITE,
    padding: '0.25rem',
    fontFamily: "'Montserrat', sans-serif",
  },
});

/**
 * The dropdown chevron from the designs, replacing MUI's default arrow icon.
 *
 * The outer span is the gold box and takes MUI's className, which carries the
 * open/closed state that rotates it. The chevron keeps its own 16x10 size and
 * is centred inside, so the box can be resized without distorting it.
 */
const ChevronIcon = ( props ) => (
  <span {...props}>
    <svg
      width='16'
      height='10'
      viewBox='0 0 16 10'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden='true'
      focusable='false'
    >
      <path d='M0 1.8656L8 9.8656L16 1.8656L14.1328 0L8 6.1328L1.8672 0L0 1.8656Z' fill='currentColor' />
    </svg>
  </span>
);

/**
 * Styles applied to the dropdown menu itself. The menu renders in a portal
 * outside the field, so it can't be reached from the styles above.
 */
const menuProps = {
  PaperProps: {
    sx: {
      borderRadius: 0,
      border: `${FIELD_BORDER_WIDTH} solid ${COLOR_BLACK}`,
      marginTop: `-${FIELD_BORDER_WIDTH}`,
      '& .MuiMenuItem-root': {
        fontFamily: "'Montserrat', sans-serif",
        color: COLOR_BLACK,
        whiteSpace: 'normal',
        '&:hover': {
          backgroundColor: alpha( COLOR_GOLD_DARK, 0.1 ),
        },
        '&.Mui-selected': {
          backgroundColor: COLOR_GOLD_DARK,

          '&:hover, &.Mui-focusVisible': {
            backgroundColor: COLOR_GOLD_DARK,
          },
        },
      },
    },
  },
};

/**
 * A stylised Material select field.
 * @param {Object} props - Component properties (id, label, options)
 */
const SelectField = ({
  id,
  label,
  name,
  control,
  defaultValue,
  validation,
  helperText,
  options,
  ...restProps
}) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={validation}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
      }) => (
        <CssSelectField
          select
          id={id}
          label={label}
          variant='outlined'
          value={value || defaultValue}
          onChange={onChange}
          onBlur={onBlur}
          error={!!error}
          fullWidth
          helperText={error ? error.message : helperText}
          SelectProps={{ MenuProps: menuProps, IconComponent: ChevronIcon }}
          {...restProps}
        >
          {options.map(( option ) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </CssSelectField>
      )}
    />
  );
};

export const SelectProps = {
  id: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  control: PropTypes.object,
  defaultValue: PropTypes.string,
  validation: PropTypes.object,
  helperText: PropTypes.string,
  options: PropTypes.arrayOf( PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }) ),
};

export const SelectDefaults = {
  id: '',
  label: '',
  name: '',
  control: null,
  defaultValue: '',
  validation: null,
  helperText: '',
  options: [],
};

SelectField.propTypes = SelectProps;
SelectField.defaultProps = SelectDefaults;

export default SelectField;
